/**
 * KaTeX does not support \begin{align}...\end{align} or \begin{align*}...\end{align*},
 * only \begin{aligned}...\end{aligned}.
 * See https://katex.org/docs/issues.html
 *
 * Same goes for the eqnarray environment, which should be replaced by aligned anyway
 * (see https://latex.wikia.org/wiki/Align_(environment) ).
 *
 * Same for split environment. See
 * https://github.com/KaTeX/KaTeX/issues/1345
 *
 * MathJax seemed to treat those interchangeably (for our use case), so these are simple conversions
 * from the former to the latter.
 *
 * A better way to handle this would be to define a custom KaTeX environment, but those functions
 * are not exposed for use at runtime, so it would require us to maintain our own fork of KaTeX.
 *
 * KaTeX may eventually add this on their end. There are a number of related issues on Github.
 */
const RE_ALIGN = /\\(begin|end)\s*{\s*(align|eqnarray|split)\*?\s*}/g;
function replaceAlign(_, macro) {
  return `\\${macro}{aligned}`;
}

/**
 * We have a lot of content where \begin{array} is used incorrectly. Namely, no column format arg
 * is specified, but it is required in KaTeX. Fix these up by putting a default {c} arg.
 */
const RE_ARRAY = /\\begin{\s*array\s*}\s*[^{\s]/g;
function replaceArray(match) {
  return `\\begin{array}{c}${match[match.length - 1]}`;
}

/**
 * We are already in equation mode, so \begin{equation} is superflous. And KaTeX doesn't support it.
 */
const RE_EQUATION = /\\(begin|end){\s*equation\s*}/g;
function removeEquation(_) {
  return '';
}

/**
 * KaTeX does not allow unescaped $ or % signs, since these have special meanings in LaTeX.
 * We escape them with a backslash here.
 */
const RE_SPECIAL_SYMBOLS = /(.*?)([$%])/g;
function replaceSpecial(_, start, symbol) {
  return start.endsWith('\\') ? `${start}${symbol}` : `${start}\\${symbol}`;
}

/**
 * KaTeX does not render \text{_____} for some reason, but a lot of our content uses it for things
 * like "Fill in the ______".
 * See https://github.com/KaTeX/KaTeX/issues/1676
 *
 * This escapes certain special characters like _ and # inside of \text{...} blocks.
 */
const RE_TEXT = /\\text(?:rm|bf|it|md|normal|sf|tt|up)?{([^}]*?)}/g;
const RE_TEXT_SPECIAL = /(.*?)([_#&])/g;
function replaceText(text) {
  return text.replace(RE_TEXT_SPECIAL, replaceSpecial);
}

/**
 * KaTeX does not allow certain unicode symbols for some dumb reason.
 */
const RE_UNICODE = /\u2013|\\u2013|\u2212|\\u2212/g;
const UNICODE_MAP = {
  '\u2013': '-',
  '\\u2013': '-',
  '\u2212': '-',
  '\\u2212': '-',
};
function replaceUnicode(unicode) {
  return UNICODE_MAP[unicode] || unicode;
}

/**
 * KaTeX does not allow \require, however, several common things like \cancel don't need to be
 * required, and are already implemented.
 */
const RE_REQUIRE = /\\require{[^}]*?}/g;
function replaceRequire(_) {
  return '';
}

/**
 * Colors. KaTeX does not expose `defineFunction` to us, so we can't easily override the builtin
 * color commands.
 * It also doesn't yet implement `\definecolor` (watch https://github.com/KaTeX/KaTeX/issues/750),
 * which would probably allow us to get rid of this regex hack.
 */
const COLOR_MAP = {
  lightblue: '#95D3FE',
  blue: '#3D99F6',
  darkblue: '#0C6AC7',
  seagreen: '#1E93A5',
  aqua: '#27D2E7',
  lightgreen: '#67D94C',
  green: '#20A900',
  darkgreen: '#456461',
  yellow: '#CEBB00',
  lightyellow: '#FEDF53',
  yelloworange: '#FCA04A',
  orange: '#EC7300',
  redorange: '#FF664D',
  red: '#D61F06',
  pink: '#E81990',
  rosepink: '#F970BF',
  violet: '#BA33D6',
  amethyst: '#625CDB',
  indigo: '#302B94',
  purple: '#69047E',
  paleyellow: '#FFF7E6',
  beige: '#FBE6B8',
  sandybrown: '#FEDABE',
  peachy: '#FCC9A1',
  tan: '#DBA97F',
  lightbrown: '#B4825D',
  mediumlightbrown: '#AA7956',
  bronze: '#815331',
  brown: '#624F41',
  white: '#FFFFFF',
  lightgray: '#FBFBFB',
  mediumlightgray: '#F0F0F0',
  mediumgray: '#D8D8D8',
  gray: '#BBBBBB',
  mediumdarkgray: '#919191',
  darkgray: '#777777',
  black: '#333333',
  darkblack: '#161616',
};
function colorLookup(color) {
  const cleanColor = color.trim().replace(/\\/g, '').toLowerCase();
  return COLOR_MAP[cleanColor] || cleanColor;
}
const RE_COLOR = /\\(color|colorbox|textcolor)\s*{(.*?)}/g;
function replaceColor(_, macro, color) {
  return `\\${macro}{${colorLookup(color)}}`;
}
const RE_FCOLORBOX = /\\fcolorbox\s*{(.*?)}\s*{(.*?)}/g;
function replaceFcolorbox(_, borderColor, bgColor) {
  return `\\fcolorbox{${colorLookup(borderColor)}}{${colorLookup(bgColor)}}`;
}

function replace(latex) {
  return latex
    .replace(RE_UNICODE, replaceUnicode)
    .replace(RE_REQUIRE, replaceRequire)
    .replace(RE_EQUATION, removeEquation)
    .replace(RE_ALIGN, replaceAlign)
    .replace(RE_ARRAY, replaceArray)
    .replace(RE_SPECIAL_SYMBOLS, replaceSpecial)
    .replace(RE_COLOR, replaceColor)
    .replace(RE_FCOLORBOX, replaceFcolorbox)
    .replace(RE_TEXT, replaceText);
}

module.exports = { replace: replace };
